import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { ApiError } from '@/api/envelope';
import { portalResetPassword } from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { PasswordField } from '@/components/ui/PasswordField';

import { AuthLayout } from './AuthLayout';
import { resetPasswordSchema, type ResetPasswordValues } from './schemas';

/**
 * The backend emails a four-digit code, not a link, so this screen asks for the
 * code rather than reading a token out of the address.
 *
 * The email address is carried over from the previous screen when the person
 * came from there, and is editable in case they did not.
 */
export function ResetPasswordPage() {
  const location = useLocation();
  const emailFromRequest = (location.state as { email?: string } | null)?.email ?? '';
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: emailFromRequest,
      code: '',
      password: '',
      confirmPassword: '',
    },
  });

  const submit = useMutation({
    mutationFn: async (values: ResetPasswordValues) => {
      await portalResetPassword({
        body: {
          email: values.email,
          code: values.code,
          new_password: values.password,
        },
        throwOnError: true,
      });
    },
    onMutate: () => setFormError(null),
    onSuccess: () => setDone(true),
    onError: (error) =>
      setFormError(
        // serverMessage, not message: message falls back to generic wording,
        // and this screen's own wording is more use than "that request could
        // not be completed".
        (error instanceof ApiError ? error.serverMessage : null) ??
          'That code is not valid or has expired. Please request a new one.',
      ),
  });

  if (done) {
    return (
      <AuthLayout title="Password changed">
        <Alert severity="success">You can now sign in with your new password.</Alert>
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Link component={RouterLink} to={ROUTES.signIn} variant="body2">
            Go to sign in
          </Link>
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      description="Enter the code we emailed you, then pick a new password."
      error={formError}
      footer={
        <Link component={RouterLink} to={ROUTES.forgotPassword} variant="body2">
          Send the code again
        </Link>
      }
    >
      <Box
        component="form"
        onSubmit={(event) => void handleSubmit((values) => submit.mutate(values))(event)}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        noValidate
      >
        <Field label="Email address" error={errors.email?.message}>
          <TextField type="email" autoComplete="username" {...register('email')} />
        </Field>

        <Field label="Verification code" error={errors.code?.message}>
          <TextField
            inputMode="numeric"
            autoComplete="one-time-code"
            // Four digits is the whole code, so there is nothing to type past it.
            slotProps={{ htmlInput: { maxLength: 4 } }}
            {...register('code')}
          />
        </Field>

        <Field
          label="New password"
          hint="At least 8 characters, with an upper case letter, a lower case letter and a digit."
          error={errors.password?.message}
        >
          <PasswordField autoComplete="new-password" {...register('password')} />
        </Field>

        <Field label="Type it again" error={errors.confirmPassword?.message}>
          <PasswordField autoComplete="new-password" {...register('confirmPassword')} />
        </Field>

        <Button type="submit" loading={submit.isPending}>
          Change my password
        </Button>
      </Box>
    </AuthLayout>
  );
}
